import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setMsg("No verification token provided."); return; }
    (async () => {
      try {
        await api.get(`/auth/verify-email`, { params: { token } });
        setState("ok");
        refreshUser();
      } catch (e) {
        setState("error");
        setMsg(apiErr(e));
      }
    })();
  }, [token, refreshUser]);

  return (
    <div className="min-h-[calc(100vh-110px)] flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center bg-white rounded-3xl border border-slate-200 p-10 shadow-soft" data-testid="verify-email-card">
        {state === "loading" && (<>
          <Loader2 className="w-12 h-12 text-brand-red animate-spin mx-auto mb-5" />
          <h1 className="font-serif text-3xl text-brand-navy">Verifying your email…</h1>
        </>)}
        {state === "ok" && (<>
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
          <h1 className="font-serif text-3xl text-brand-navy">Email verified!</h1>
          <p className="mt-3 text-slate-600">Your account is now active. A welcome email is on its way. You can start your application right away.</p>
          <Link to="/dashboard" data-testid="verify-dashboard-btn" className="mt-6 inline-block w-full px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors">Go to dashboard</Link>
        </>)}
        {state === "error" && (<>
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5"><XCircle className="w-8 h-8 text-brand-red" /></div>
          <h1 className="font-serif text-3xl text-brand-navy">Verification failed</h1>
          <p className="mt-3 text-slate-600">{msg}</p>
          <Link to="/dashboard" className="mt-6 inline-block w-full px-6 py-3.5 rounded-full bg-brand-navy text-white font-semibold hover:bg-slate-800 transition-colors">Go to dashboard</Link>
        </>)}
      </motion.div>
    </div>
  );
}
